import React from 'react';

interface ButtonProps {
  variant?: 'primary' | 'secondary' | 'success' | 'danger' | 'ghost';
  size?: 'sm' | 'md' | 'lg';
  fullWidth?: boolean;
  loading?: boolean;
  children: React.ReactNode;
  className?: string;
  disabled?: boolean;
  onClick?: (event: React.MouseEvent<HTMLButtonElement>) => void;
  type?: 'button' | 'submit' | 'reset';
}

export default function Button({
  variant = 'primary',
  size = 'md',
  fullWidth = false,
  loading = false,
  children,
  className = '',
  disabled,
  onClick,
  type = 'button'
}: ButtonProps) {
  const baseClasses = 'inline-flex items-center justify-center font-medium rounded-md transition-all duration-200 focus:ring-2 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed';

  const variants = {
    primary: 'bg-trust-blue text-pure-white hover:bg-trust-blue/90 focus:ring-trust-blue/20 shadow-button hover:shadow-button-hover',
    secondary: 'bg-wellness-mint text-pure-white hover:bg-wellness-mint/90 focus:ring-wellness-mint/20 shadow-button hover:shadow-button-hover',
    success: 'bg-success-green text-pure-white hover:bg-success-green/90 focus:ring-success-green/20 shadow-button hover:shadow-button-hover',
    danger: 'bg-error-red text-pure-white hover:bg-error-red/90 focus:ring-error-red/20 shadow-button hover:shadow-button-hover',
    ghost: 'bg-transparent text-trust-blue hover:bg-trust-blue/5 focus:ring-trust-blue/20'
  };

  const sizes = {
    sm: 'px-3 py-2 text-supporting',
    md: 'px-4 py-3 text-button',
    lg: 'px-6 py-4 text-button'
  };

  const widthClass = fullWidth ? 'w-full' : '';

  const classes = `${baseClasses} ${variants[variant]} ${sizes[size]} ${widthClass} ${className}`;

  return (
    <button
      className={classes}
      disabled={disabled || loading}
      onClick={onClick}
      type={type}
    >
      {loading && (
        <svg className="animate-spin -ml-1 mr-2 h-4 w-4" fill="none" viewBox="0 0 24 24">
          <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
          <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
        </svg>
      )}
      {children}
    </button>
  );
}